import { useEffect, useState, type MouseEvent } from 'react';
import { text, useLanguage } from '../../i18n';
import type { PricePromotion } from '../../api';
import {
  isMissingRoute,
  msUntilNextPromotionBoundary,
  usePricePromotionsQuery,
} from '../../queries';
import { navigate } from '../../router';
import {
  formatDayClock,
  formatPercentOff,
  parseInstant,
  percentOffOrNull,
} from '../priceDiscounts';

/**
 * Holds the signature of the promotions last dismissed: each promotion's id,
 * percent, end and models. A new, removed or changed promotion (extended,
 * deeper, more models) shows the banner again; a recurring promotion moving
 * between its windows does not.
 */
export const PROMOTION_BANNER_DISMISSED_KEY = 'antix.promotionBannerDismissed';
const MAX_LISTED_MODELS = 3;

/**
 * Shell banner announcing price promotions in force or starting soon. Purely
 * informational: it shows the last list it loaded (a failed refresh keeps
 * it), and renders nothing before a first successful load.
 */
export function PromotionBanner({ enabled }: { enabled: boolean }) {
  useLanguage();
  const { data, error } = usePricePromotionsQuery(enabled);
  // A route that has gone missing (404, e.g. a backend rollback) withdraws
  // what it announced; any other failed refresh keeps the last list.
  const fetched = isMissingRoute(error) ? [] : (data ?? []);
  // Re-render at the next start or end, so between refreshes an upcoming
  // promotion turns active and an ended one disappears on time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const delay = msUntilNextPromotionBoundary(fetched, now);
    if (delay === null) return undefined;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(delay + 50, 2 ** 31 - 1));
    return () => window.clearTimeout(timer);
  }, [fetched, now]);
  // A promotion that has ended since the last fetch is never announced, and
  // one that has started since is announced as active.
  const promotions = fetched
    .filter((promotion) => {
      const end = parseInstant(promotion.ends_at);
      return !end || end.getTime() > now;
    })
    .map((promotion) => {
      // Only a one-off promotion is known to apply once it starts; a
      // recurring one applies inside its windows, which only the backend
      // judges, so it waits for the next refresh.
      const start = parseInstant(promotion.starts_at);
      return !promotion.active && !promotion.recurring && start && start.getTime() <= now
        ? { ...promotion, active: true }
        : promotion;
    });
  const signature = dismissalSignature(promotions);
  const [dismissed, setDismissed] = useState(readDismissed);
  // Expansion belongs to one set of promotions: a changed set starts collapsed.
  const [expandedFor, setExpandedFor] = useState('');
  const expanded = expandedFor === signature;

  // Dismissing in another tab hides the banner here too.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === PROMOTION_BANNER_DISMISSED_KEY) setDismissed(event.newValue ?? '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!enabled || promotions.length === 0 || dismissed === signature) return null;

  const [first, ...rest] = promotions;
  const others = rest.length;
  const dismiss = () => {
    try {
      localStorage.setItem(PROMOTION_BANNER_DISMISSED_KEY, signature);
    } catch {
      // Unavailable storage only means the banner returns on the next load.
    }
    setDismissed(signature);
  };

  return (
    <section
      className="promotion-banner"
      aria-label={text('价格优惠', 'Price promotions', '価格プロモーション')}
    >
      <span className="promotion-banner-icon" aria-hidden="true">
        %
      </span>
      <div className="promotion-banner-message">
        <p>
          {promotionMessage(first)}
          {others > 0 ? (
            <>
              {' · '}
              <button
                className="promotion-banner-more"
                type="button"
                aria-expanded={expanded}
                aria-controls="promotion-banner-rest"
                onClick={() => setExpandedFor(expanded ? '' : signature)}
              >
                {expanded
                  ? text('收起', 'Show less', '閉じる')
                  : text(
                      `另有 ${others} 项优惠`,
                      `+${others} more ${others === 1 ? 'promotion' : 'promotions'}`,
                      `ほか ${others} 件のプロモーション`,
                    )}
              </button>
            </>
          ) : null}
        </p>
        <div id="promotion-banner-rest" hidden={!expanded}>
          {expanded
            ? rest.map((promotion) => <p key={promotion.id}>{promotionMessage(promotion)}</p>)
            : null}
        </div>
      </div>
      <a className="promotion-banner-link" href="/models" onClick={openModels}>
        {text('查看价格', 'See prices', '価格を見る')}
      </a>
      <button
        className="promotion-banner-dismiss"
        type="button"
        aria-label={text('关闭优惠提示', 'Dismiss promotion', 'プロモーションの通知を閉じる')}
        onClick={dismiss}
      >
        <span aria-hidden="true">×</span>
      </button>
    </section>
  );
}

export function promotionMessage(promotion: PricePromotion) {
  const models = listedModels(promotion.models);
  const percent = percentOffOrNull(promotion.percent_off);
  const pct = percent === null ? '' : formatPercentOff(percent);
  const deal = pct
    ? text(`${models} ${pct}`, `${pct} on ${models}`, `${models} が ${pct}`)
    : text(`${models} 特价`, `Special pricing on ${models}`, `${models} が特別価格`);
  const recurring = promotion.recurring
    ? text('，仅限特定日期/时段', ' on selected days/hours', '（特定の日・時間帯のみ）')
    : '';

  if (!promotion.active) {
    const start = parseInstant(promotion.starts_at);
    // A start already past (a recurring promotion between its windows, as of
    // a stale list) is not "coming": fall through to the "until" wording.
    if (start && start.getTime() <= Date.now()) return untilMessage(promotion, deal, recurring);
    if (!start) return `${deal}${recurring}`;
    const when = formatDayClock(start);
    return text(
      `${when} 起：${deal}${recurring}`,
      `Coming ${when}: ${deal}${recurring}`,
      `${when} から：${deal}${recurring}`,
    );
  }

  return untilMessage(promotion, deal, recurring);
}

function untilMessage(promotion: PricePromotion, deal: string, recurring: string) {
  const end = parseInstant(promotion.ends_at);
  if (!end) return `${deal}${recurring}`;
  const until = formatDayClock(end);
  return text(
    `${deal}，至 ${until}（本地时间）${recurring}`,
    `${deal} until ${until} (local)${recurring}`,
    `${deal}（${until} まで・現地時間）${recurring}`,
  );
}

function listedModels(models: string[]) {
  const shown = models.slice(0, MAX_LISTED_MODELS);
  const rest = models.length - shown.length;
  const list = shown.join(text('、', ', ', '、'));
  if (rest <= 0) return list;
  return text(
    `${list} 等 ${models.length} 个模型`,
    `${list} +${rest} more`,
    `${list} ほか ${rest} モデル`,
  );
}

function dismissalSignature(promotions: PricePromotion[]) {
  return promotions
    .map((p) => [p.id, p.percent_off ?? '', p.ends_at, [...p.models].sort().join('+')].join('|'))
    .sort()
    .join(',');
}

function readDismissed() {
  try {
    return localStorage.getItem(PROMOTION_BANNER_DISMISSED_KEY) ?? '';
  } catch {
    return '';
  }
}

function openModels(event: MouseEvent<HTMLAnchorElement>) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate('/models');
}
